import React, { useState } from "react";
import { Play, ExternalLink } from "lucide-react";

/**
 * VideoFeature - performant YouTube facade.
 * Loads only a thumbnail until the user clicks play; the iframe is mounted on demand.
 * This protects Core Web Vitals (LCP/CLS) while still giving Google a rich VideoObject signal.
 */
const VideoFeature = ({
  videoId,
  title,
  description,
  badge,
  poster,
  watchUrl,
  ariaLabel,
  testIdPrefix = "video-feature",
}) => {
  const [playing, setPlaying] = useState(false);
  const thumb = poster || `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  const fallbackThumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const href = watchUrl || `https://www.youtube.com/watch?v=${videoId}`;

  return (
    <figure
      data-testid={`${testIdPrefix}-figure`}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0F0F23] shadow-[0_30px_80px_-30px_rgba(0,212,255,0.18)]"
    >
      <div className="relative aspect-video w-full bg-black">
        {playing ? (
          <iframe
            data-testid={`${testIdPrefix}-iframe`}
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <button
            type="button"
            data-testid={`${testIdPrefix}-play-button`}
            onClick={() => setPlaying(true)}
            aria-label={ariaLabel || `${title} — afspelen`}
            className="group absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF]"
          >
            <img
              src={thumb}
              onError={(e) => {
                if (e.currentTarget.src !== fallbackThumb) e.currentTarget.src = fallbackThumb;
              }}
              alt={title}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-[#050509]/70 via-transparent to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-[#00D4FF] text-[#050509] shadow-2xl group-hover:scale-110 transition-transform duration-300">
                <span className="absolute inset-0 rounded-full bg-[#00D4FF] opacity-50 group-hover:opacity-0 animate-ping" />
                <Play className="relative w-8 h-8 ml-1 fill-current" strokeWidth={0} />
              </span>
            </span>
            {badge && (
              <span
                data-testid={`${testIdPrefix}-badge`}
                className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#050509]/80 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-white border border-white/15"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF]" />
                {badge}
              </span>
            )}
          </button>
        )}
      </div>
      <figcaption className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-t border-white/10">
        <div className="max-w-2xl">
          <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-1.5">{title}</h3>
          {description && (
            <p className="text-sm text-white/60 leading-relaxed">{description}</p>
          )}
        </div>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testIdPrefix}-watch-link`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/70 hover:text-[#00D4FF] transition-colors whitespace-nowrap"
        >
          YouTube <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </figcaption>
    </figure>
  );
};

export default VideoFeature;
